import Button from '@components/Button';
import SortableList from '@components/SortableList';
import Tip from '@components/Tip';
import { TipWrapper } from '@components/TipWrapper';
import Title from '@components/Title';
import IconExit from '@components/icons/IconExit';
import IconTrash from '@components/icons/IconTrash';
import useToggle from '@hooks/useToggle';
import { $sceneMapApi } from '@stores/dataStores';
import { $gateHotspotLayerMode } from '@stores/hotspotStores';
import { MainPlayer } from '@stores/krpPlayerStores';
import type { SceneConfigLayers, SceneData } from '@types';
import arrayMove from '@utils/arrayMove';
import { useGate } from 'effector-react';
import difference from 'ramda/es/difference';
import equals from 'ramda/es/equals';
import { useEffect, useRef, useState } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import { type SortEnd, SortableElement } from 'react-sortable-hoc';
import { onPlayerViewChange } from '../../player/actions/viewActions';
import SidebarHeader from '../../sidebar/components/SidebarHeader';
import { krpAddHotspot, krpRemoveHotspot } from '../actions';
import { PanelLayerSetting } from './PanelLayerSetting';
import { getLayerHotspots } from './getLayerHotspots';

interface Props {
  scene: SceneData;
  onQuitClick: () => void;
}

const MAX = 5;

export function HotspotLayerModeList({ scene, onQuitClick }: Props) {
  const hotspotList = scene.hotspot ?? [];
  const configLayer = scene.config.layers ?? {};
  const layerList = configLayer.list ?? [];
  const loading = useToggle(false);
  const [editingIndex, setEditingIndex] = useState<null | number>(null);
  const lastAddingList = useRef<string[]>([]);
  useGate($gateHotspotLayerMode, true);

  const changeLayer = (fov: number) => {
    const list = getLayerHotspots({ fov, hotspotList, layerList });
    if (!equals(lastAddingList.current, list)) {
      const added = difference(list, lastAddingList.current);
      const removed = difference(lastAddingList.current, list);
      removed.forEach(krpRemoveHotspot);
      added.forEach((id) => {
        const hotspot = hotspotList.find((item) => item.id === id);
        if (hotspot) {
          krpAddHotspot(hotspot);
        }
      });
      lastAddingList.current = list;
    }
  };

  useEffect(() => {
    changeLayer(MainPlayer.get()?.getView()?.fov ?? 0);
    const unwatch = onPlayerViewChange.watch(({ fov }) => {
      changeLayer(fov);
    });
    return () => {
      unwatch();
    };
  }, [layerList]);

  const addLayer = () => {
    loading.on();
    setEditingIndex(layerList.length);
    loading.off();
  };

  const handleSortEnd = (sort: SortEnd) => {
    $sceneMapApi.updateScene({
      id: scene.id,
      updater: (draft) => {
        const list = draft.config.layers?.list;
        if (!list) return;
        draft.config.layers!.list = arrayMove({
          array: list,
          fromIndex: sort.oldIndex,
          toIndex: sort.newIndex,
        });
      },
    });
  };

  const handleDelete = (index: number) => {
    $sceneMapApi.updateScene({
      id: scene.id,
      updater: (draft) => {
        const list = draft.config.layers?.list;
        if (!list) return;
        list.splice(index, 1);
      },
    });
  };

  return (
    <>
      <SidebarHeader
        title={
          <div className="flex">
            <Title>热点</Title>
            <Tip className="ml-10 self-end text-base">{`显示范围 ${layerList.length}/${MAX}`}</Tip>
          </div>
        }
        btns={
          <TipWrapper text="退出操作" placement="bottom" size="sm">
            <IconExit className="focus:outline-none" onClick={onQuitClick} />
          </TipWrapper>
        }
      />
      <Button
        size="lg"
        className="mb-10 w-full"
        disabled={loading.isOn || layerList.length >= MAX}
        onClick={addLayer}
      >
        添加显示层级
      </Button>
      {layerList.length > 0 ? (
        <div className="relative flex-1">
          <SortableList
            axis="y"
            lockAxis="y"
            distance={10}
            onSortEnd={handleSortEnd}
          >
            <Scrollbars>
              {layerList.map((x, i) => (
                <Item
                  index={i}
                  listIndex={i}
                  key={x.id}
                  data={x}
                  onClick={setEditingIndex}
                  onDeleteClick={handleDelete}
                />
              ))}
            </Scrollbars>
          </SortableList>
        </div>
      ) : (
        <>
          <Tip className="mt-5 text-center text-base">
            {`每个场景最多可添加${MAX}组显示层级`}
          </Tip>
          <Tip className="mt-5 text-center text-base">
            添加后支持上下拖拽调整层级顺序
          </Tip>
        </>
      )}
      <Button scheme="darkest" className="mt-auto w-full" onClick={onQuitClick}>
        退出编辑
      </Button>
      {editingIndex !== null && (
        <PanelLayerSetting
          scene={scene}
          index={editingIndex}
          onRequestClose={() => setEditingIndex(null)}
        />
      )}
    </>
  );
}

type ItemProps = {
  data: Required<SceneConfigLayers>['list'][number];
  listIndex: number;
  onClick: (index: number) => void;
  onDeleteClick: (index: number) => void;
};

const Item = SortableElement(
  ({ data, listIndex, onClick, onDeleteClick }: ItemProps) => {
    const { hotspotIds, fovMin, fovMax } = data;
    return (
      <div
        className="mb-10 flex h-35 cursor-pointer items-center rounded bg-black-#34 px-10 text-base text-white"
        onClick={() => onClick(listIndex)}
      >
        <div className="mr-10 truncate">{`FOV ${fovMin}-${fovMax}`}</div>
        <div className="ml-auto shrink-0 text-black-gray">{`${hotspotIds.length}热点`}</div>
        <TipWrapper text="删除" placement="bottom" className="ml-10" size="sm">
          <IconTrash
            className="shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteClick(listIndex);
            }}
          />
        </TipWrapper>
      </div>
    );
  }
);
